import React, { useState, useEffect } from 'react';
import { Heart, MapPin, Star, Award, Crown, Medal, Calendar, Users, Wifi, Car, Coffee, Waves, Mountain, Building, TreePine, Search, Filter, ChevronDown, Sparkles, Shield, CheckCircle, Calculator, Globe, Gift, Camera, Zap, ExternalLink, Phone, Mail, Home, ArrowRight, BarChart3, Target, Clock, Lightbulb, Brain, TrendingUp, ArrowLeft, Eye, Bookmark, Share2 } from 'lucide-react';

const PortfolioPage = () => {
  const [selectedPersona, setSelectedPersona] = useState('family');
  const [selectedDestination, setSelectedDestination] = useState('mallorca');
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Mock property data based on Clara Top 10 system
  const mockProperties = [
    {
      id: '1',
      name: 'Villa Serenity Mallorca',
      address: 'Cala Millor, Mallorca, Spain',
      image: 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=800&h=400&fit=crop',
      nightlyFrom: 280,
      reviewScore: 9.2,
      reviews: 147,
      distanceKm: 2.1,
      amenities: ['Private Pool', 'Sea View', 'Free WiFi', 'Kitchen', 'Air Conditioning'],
      personaScores: { family: 95, wellness: 88, workation: 82, adventure: 79 },
      claraScore: 92,
      evidence: '9.2/10 (147 reviews) • Private Pool • Sea View • Free WiFi',
      certification: { level: 'silver', name: 'Clara Silver', badge: '⭐⭐' },
      badges: ['Excellent', 'Pool', 'Sea View'],
      ctaUrl: '#'
    },
    {
      id: '2',
      name: 'Casa Mediterránea Premium',
      address: 'Port de Sóller, Mallorca, Spain',
      image: 'https://images.unsplash.com/photo-1582268611958-ebfd161ef9cf?w=800&h=400&fit=crop',
      nightlyFrom: 320,
      reviewScore: 9.4,
      reviews: 203,
      distanceKm: 0.8,
      amenities: ['Spa Access', 'Mountain View', 'Wellness Center', 'Yoga Studio', 'Organic Garden'],
      personaScores: { family: 87, wellness: 96, workation: 85, adventure: 82 },
      claraScore: 94,
      evidence: '9.4/10 (203 reviews) • Spa Access • Mountain View • Wellness Center',
      certification: { level: 'silver', name: 'Clara Silver', badge: '⭐⭐' },
      badges: ['Exceptional', 'Spa', 'City Center'],
      ctaUrl: '#'
    },
    {
      id: '3',
      name: 'Modern Workation Hub',
      address: 'Palma de Mallorca, Spain',
      image: 'https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=800&h=400&fit=crop',
      nightlyFrom: 195,
      reviewScore: 8.9,
      reviews: 89,
      distanceKm: 1.2,
      amenities: ['High-Speed WiFi', 'Dedicated Workspace', 'Business Center', 'Meeting Room', 'Rooftop Terrace'],
      personaScores: { family: 78, wellness: 81, workation: 93, adventure: 75 },
      claraScore: 89,
      evidence: '8.9/10 (89 reviews) • High-Speed WiFi • Dedicated Workspace • Business Center',
      certification: { level: 'bronze', name: 'Clara Bronze', badge: '⭐' },
      badges: ['Excellent', 'Free WiFi', 'City Center'],
      ctaUrl: '#'
    },
    {
      id: '4',
      name: 'Adventure Base Camp',
      address: 'Alcúdia, Mallorca, Spain',
      image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&h=400&fit=crop',
      nightlyFrom: 240,
      reviewScore: 9.1,
      reviews: 156,
      distanceKm: 3.5,
      amenities: ['Bike Rental', 'Hiking Trails', 'Water Sports', 'Outdoor Equipment', 'Beach Access'],
      personaScores: { family: 85, wellness: 79, workation: 76, adventure: 94 },
      claraScore: 91,
      evidence: '9.1/10 (156 reviews) • Bike Rental • Hiking Trails • Water Sports',
      certification: { level: 'silver', name: 'Clara Silver', badge: '⭐⭐' },
      badges: ['Excellent', 'Near Center', 'Pool'],
      ctaUrl: '#'
    },
    {
      id: '5',
      name: 'Luxury Family Resort',
      address: 'Cala d\'Or, Mallorca, Spain',
      image: 'https://images.unsplash.com/photo-1571003123894-1f0594d2b5d9?w=800&h=400&fit=crop',
      nightlyFrom: 380,
      reviewScore: 9.6,
      reviews: 284,
      distanceKm: 1.8,
      amenities: ['Kids Club', 'Family Pool', 'Playground', 'Baby Equipment', 'Family Suites'],
      personaScores: { family: 97, wellness: 84, workation: 79, adventure: 86 },
      claraScore: 96,
      evidence: '9.6/10 (284 reviews) • Kids Club • Family Pool • Playground',
      certification: { level: 'gold', name: 'Clara Gold', badge: '⭐⭐⭐' },
      badges: ['Exceptional', 'Pool', 'Near Center'],
      ctaUrl: '#'
    }
  ];

  useEffect(() => {
    // Simulate API call
    setLoading(true);
    setTimeout(() => {
      setProperties(mockProperties);
      setLoading(false);
    }, 1000);
  }, [selectedPersona, selectedDestination]);

  const getPersonaColor = (persona) => {
    const colors = {
      family: 'from-blue-500 to-blue-600',
      wellness: 'from-green-500 to-emerald-600',
      workation: 'from-purple-500 to-indigo-600',
      adventure: 'from-orange-500 to-red-600'
    };
    return colors[persona] || colors.family;
  };

  const getPersonaIcon = (persona) => {
    const icons = {
      family: <Users className="w-5 h-5" />,
      wellness: <Heart className="w-5 h-5" />,
      workation: <Lightbulb className="w-5 h-5" />,
      adventure: <Mountain className="w-5 h-5" />
    };
    return icons[persona] || icons.family;
  };

  const getCertificationColor = (level) => {
    const colors = {
      gold: 'from-yellow-400 to-yellow-600',
      silver: 'from-gray-400 to-gray-600',
      bronze: 'from-amber-600 to-amber-800',
      standard: 'from-blue-400 to-blue-600'
    };
    return colors[level] || colors.standard;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-100 via-gray-100 to-stone-200 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-gray-300 border-t-gray-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading Clara Top 10 Properties...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-100 via-gray-100 to-stone-200">
      {/* Header */}
      <div className="bg-gradient-to-br from-stone-100 via-gray-100 to-stone-200 py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between mb-8">
            <button 
              onClick={() => window.history.back()}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back to HeartPlaces</span>
            </button>
            
            <div className="flex items-center space-x-4">
              <button className="p-2 bg-white/80 backdrop-blur-sm rounded-full hover:bg-white transition-colors">
                <Share2 className="w-5 h-5 text-gray-600" />
              </button>
              <button className="p-2 bg-white/80 backdrop-blur-sm rounded-full hover:bg-white transition-colors">
                <Bookmark className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          </div>

          <div className="text-center mb-8">
            <div className="inline-flex items-center space-x-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium text-gray-700 mb-4">
              <Sparkles className="w-4 h-4" />
              <span>Clara Top 10 Portfolio</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gray-600 via-stone-700 to-gray-800 mb-4">
              Curated Properties
            </h1>
            
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              AI-selected premium properties tailored to your persona and preferences
            </p>
          </div>

          {/* Persona Selection */}
          <div className="flex justify-center mb-8">
            <div className="bg-white/90 backdrop-blur-sm p-2 rounded-2xl shadow-lg">
              {['family', 'wellness', 'workation', 'adventure'].map((persona) => (
                <button
                  key={persona}
                  onClick={() => setSelectedPersona(persona)}
                  className={`px-6 py-3 rounded-xl font-semibold transition-all capitalize inline-flex items-center space-x-2 ${
                    selectedPersona === persona
                      ? `bg-gradient-to-r ${getPersonaColor(persona)} text-white shadow-lg`
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {getPersonaIcon(persona)}
                  <span>{persona}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Properties Grid */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="space-y-6">
          {properties.map((property, index) => (
            <div key={property.id} className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
              <div className="flex flex-col lg:flex-row">
                {/* Image Section */}
                <div className="lg:w-2/5 relative">
                  <img 
                    src={property.image} 
                    alt={property.name}
                    className="w-full h-64 lg:h-80 object-cover"
                  />
                  
                  {/* Clara Score Badge */}
                  <div className="absolute top-4 left-4">
                    <div className={`bg-gradient-to-r ${getCertificationColor(property.certification.level)} text-white px-3 py-1 rounded-full text-sm font-bold shadow-lg`}>
                      {property.certification.badge} {property.claraScore}
                    </div>
                  </div>
                  
                  {/* Rank Badge */}
                  <div className="absolute top-4 right-4">
                    <div className="bg-gray-900/80 text-white px-3 py-1 rounded-full text-sm font-bold">
                      #{index + 1}
                    </div>
                  </div>
                  
                  {/* Heart Icon */}
                  <button className="absolute bottom-4 right-4 p-2 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white transition-colors">
                    <Heart className="w-5 h-5 text-gray-600 hover:text-red-500 transition-colors" />
                  </button>
                </div>

                {/* Content Section */}
                <div className="lg:w-3/5 p-8">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">
                        {property.name}
                      </h3>
                      <div className="flex items-center text-gray-600 mb-2">
                        <MapPin className="w-4 h-4 mr-1" />
                        <span className="text-sm">{property.address}</span>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <div className="text-2xl font-bold text-gray-900">
                        €{property.nightlyFrom}
                      </div>
                      <div className="text-sm text-gray-600">per night</div>
                    </div>
                  </div>

                  {/* Review Score */}
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="flex items-center space-x-1">
                      <Star className="w-5 h-5 text-yellow-500 fill-current" />
                      <span className="font-semibold">{property.reviewScore}</span>
                      <span className="text-gray-600">({property.reviews} reviews)</span>
                    </div>
                    
                    {property.distanceKm && (
                      <div className="flex items-center space-x-1 text-gray-600">
                        <MapPin className="w-4 h-4" />
                        <span className="text-sm">{property.distanceKm}km to center</span>
                      </div>
                    )}
                  </div>

                  {/* Badges */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {property.badges.map((badge, i) => (
                      <span key={i} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">
                        {badge}
                      </span>
                    ))}
                  </div>

                  {/* Amenities */}
                  <div className="mb-6">
                    <div className="flex flex-wrap gap-2">
                      {property.amenities.slice(0, 5).map((amenity, i) => (
                        <span key={i} className="px-2 py-1 bg-stone-100 text-stone-700 rounded text-xs">
                          {amenity}
                        </span>
                      ))}
                      {property.amenities.length > 5 && (
                        <span className="px-2 py-1 bg-stone-100 text-stone-700 rounded text-xs">
                          +{property.amenities.length - 5} more
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Persona Scores */}
                  <div className="mb-6">
                    <div className="text-sm text-gray-600 mb-2">Persona Match Scores:</div>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                      {Object.entries(property.personaScores).map(([persona, score]) => (
                        <div key={persona} className="text-center">
                          <div className={`text-lg font-bold ${selectedPersona === persona ? 'text-blue-600' : 'text-gray-700'}`}>
                            {score}
                          </div>
                          <div className="text-xs text-gray-500 capitalize">{persona}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Clara Evidence */}
                  <div className="mb-6">
                    <div className="text-sm text-gray-600 mb-1">Clara AI Analysis:</div>
                    <div className="text-sm text-gray-800 bg-green-50 p-3 rounded-lg">
                      {property.evidence}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex space-x-4">
                    <button className="flex-1 bg-gray-700 hover:bg-gray-800 text-white px-6 py-3 rounded-xl font-semibold transition-colors inline-flex items-center justify-center space-x-2">
                      <ExternalLink className="w-5 h-5" />
                      <span>View Details</span>
                    </button>
                    
                    <button className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:border-gray-400 transition-colors inline-flex items-center space-x-2">
                      <Eye className="w-5 h-5" />
                      <span>Quick View</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Load More */}
        <div className="text-center mt-12">
          <button className="bg-gray-700 hover:bg-gray-800 text-white px-8 py-4 rounded-xl font-semibold transition-colors inline-flex items-center space-x-2">
            <span>Load More Properties</span>
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PortfolioPage;

